import type { DocNode } from '@atlaskit/adf-schema';
import { fg } from '@atlaskit/platform-feature-flags';
import type { CommandProperties } from '@atlassian/rovo-content-bridge-api';
import type { PageContentCommandResult } from '@atlassian/rovo-content-bridge-api-commands';

import { log } from '../utils/aifc-logging';
import { getDocument } from '../utils/bridge-document-utils';

import { CommandHandler, type PassThroughCommandProps } from './command-handler';

export const buildContentContext = ({
	props,
	doc,
	selectionFragment,
	selectionLocalIds,
	selectionMarkdown,
}: {
	doc: DocNode | undefined;
	props: PassThroughCommandProps;
	selectionFragment: string;
	selectionLocalIds: string;
	selectionMarkdown: string;
}): PageContentCommandResult => {
	const fullAdditionalContext = props.additionalContext || { isViewMode: false };
	const { isViewMode = false, ...additionalContext } = fullAdditionalContext;

	return {
		contentId: props.contentId,
		contentType: props.contentType,
		pageBody: doc,
		selectionFragment,
		selectionLocalIds,
		selectionMarkdown,
		isViewMode,
		useGenericEditorSkill: props.useGenericEditorSkill,
		additionalContext: Object.keys(additionalContext).length > 0 ? additionalContext : undefined,
	};
};

export class ReadContentCommandHandler extends CommandHandler<
	CommandProperties,
	PageContentCommandResult
> {
	async execute(): Promise<PageContentCommandResult> {
		if (fg('platform_editor_aifc_editor_rovo_bridge_batch_1')) {
			const defaultResult = buildContentContext({
				props: this.props,
				doc: undefined,
				selectionFragment: '',
				selectionLocalIds: '',
				selectionMarkdown: '',
			});

			try {
				const doc = await getDocument(this.props.editorAPI);
				if (!doc) {
					// TODO: EDITOR-123 - report this error
					return defaultResult;
				}

				const selection = this.props.editorAPI.aiExperience?.actions.getAISelectionContext();
				const content = buildContentContext({
					props: this.props,
					doc,
					selectionFragment: selection?.selectionFragment
						? JSON.stringify(selection.selectionFragment)
						: '',
					selectionLocalIds: selection?.localIds ? JSON.stringify(selection.localIds) : '',
					selectionMarkdown: selection?.selectionMarkdown ?? '',
				});
				log('handle read-content-command result', {
					additionalContext: this.props.additionalContext,
					doc,
					selection,
					content,
				});

				return content ?? defaultResult;
			} catch {
				// TODO: EDITOR-123 - report this error
				return defaultResult;
			}
		} else {
			const defaultResult: PageContentCommandResult = {
				contentType: 'page',
				pageBody: undefined,
				contentId: this.props.contentId,
				selectionFragment: '',
				selectionLocalIds: '',
				selectionMarkdown: '',
				isViewMode: false,
			};

			try {
				const doc = await getDocument(this.props.editorAPI);
				if (!doc) {
					// TODO: EDITOR-123 - report this error
					return defaultResult;
				}

				const selection = this.props.editorAPI.aiExperience?.actions.getAISelectionContext();
				const content = this.props.buildContentContext?.({
					doc,
					selectionFragment: selection?.selectionFragment
						? JSON.stringify(selection.selectionFragment)
						: '',
					selectionLocalIds: selection?.localIds ? JSON.stringify(selection.localIds) : '',
					selectionMarkdown: selection?.selectionMarkdown ?? '',
					useGenericEditorSkill: this.props.useGenericEditorSkill,
				});
				log('handle read-content-command result', { doc, selection, content });

				return content ?? defaultResult;
			} catch {
				// TODO: EDITOR-123 - report this error
				return defaultResult;
			}
		}
	}
}
